<script setup>
// Node customizado do canvas: card com chip de ícone tintado, título, subtítulo
// e badges derivados por tipo (modelo/raciocínio, destino da escalação, formato
// do envio, nº de categorias). SAÍDAS múltiplas: Proteções (ok/blocked),
// Condição (true/false) e Classificar (1 por categoria) abrem várias alças à
// direita. 'start' não tem entrada; 'end' não tem saída.
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { Handle, Position } from '@vue-flow/core';
import { NODE_META } from './nodeMeta';

const props = defineProps({
  // Vue Flow passa o id do node.
  id: { type: String, default: '' },
  data: { type: Object, required: true },
  // Vue Flow passa o estado de seleção do node.
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();

const meta = computed(() => NODE_META[props.data.kind] || {});
const color = computed(() => props.data.color || meta.value.color || '#9aa0a6');
// 'start' e 'prospect' são gatilhos: só saída, sem entrada.
const hasInput = computed(
  () => !['start', 'prospect'].includes(props.data.kind)
);
// O Agente tem DUAS saídas tipadas (evita confundir fluxo com ferramentas):
// direita = próximo passo do fluxo; baixo (âmbar) = ferramentas que ele usa.
const isAgent = computed(() => props.data.kind === 'agent');
// Ferramenta: a entrada fica no TOPO (a linha desce do Agente direto nela) e a
// bolinha é âmbar, igual à porta de ferramentas do Agente.
const isTool = computed(() => (props.data.kind || '').startsWith('tool_'));
// Ferramenta desativada (toggle no inspector): card apagado + badge.
const isToolOff = computed(() => isTool.value && props.data.enabled === false);

// "Solto" = sem nenhuma ligação no fluxo (vem do canvas via provide).
const floatingNodeIds = inject('floatingNodeIds', null);
const isFloating = computed(
  () => floatingNodeIds?.value?.has(props.id) || false
);

// Fundo tintado suave a partir de um hex (#RRGGBB + alpha).
const tint = (hex, alpha = '1f') => `${hex}${alpha}`;

const TONE_HEX = {
  default: '#9aa0a6',
  info: '#4285f4',
  success: '#34a853',
  warn: '#f9ab00',
  danger: '#ea4335',
};

// Badges derivados da config do node — dão um resumo visual do que ele faz.
const badges = computed(() => {
  const d = props.data;
  const out = Array.isArray(d.badges) ? [...d.badges] : [];
  switch (d.kind) {
    case 'agent':
      if (d.reasoningEffort && d.reasoningEffort !== 'off') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.REASONING'), tone: 'info' });
      }
      break;
    case 'send':
      if (d.responseFormat === 'audio') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.AUDIO'), tone: 'info' });
      } else if (d.responseFormat === 'both') {
        out.push({
          label: t('AI_AGENTS.FLOW.BADGES.TEXT_AUDIO'),
          tone: 'info',
        });
      }
      break;
    case 'tool_escalate':
      if (d.escalateTo === 'team') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.TEAM'), tone: 'warn' });
      } else if (d.escalateTo === 'user') {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.PERSON'), tone: 'warn' });
      }
      break;
    case 'http_request':
      // Integração de template ainda com endpoint placeholder -> avisa que
      // precisa plugar a URL real do sistema do cliente.
      if ((d.endpoint || '').includes('SEU-SISTEMA')) {
        out.push({ label: t('AI_AGENTS.FLOW.BADGES.CONFIGURE'), tone: 'warn' });
      }
      break;
    case 'classify': {
      const n = (d.categories || []).filter(c => c && c.name).length;
      if (n) {
        out.push({
          label: `${n} ${t('AI_AGENTS.FLOW.BADGES.CATEGORIES')}`,
          tone: 'default',
        });
      }
      break;
    }
    default:
      break;
  }
  if ((d.kind || '').startsWith('tool_') && d.enabled === false) {
    out.push({ label: t('AI_AGENTS.FLOW.TOOLS.OFF'), tone: 'default' });
  }
  return out;
});

// Lista de saídas do node (cada uma vira uma alça conectável à direita).
const outputs = computed(() => {
  const d = props.data;
  // Ferramenta é ponto final: o Agente chama e recebe o resultado, não segue
  // pra outro bloco. Sem saída = menos alça à toa, menos chance de erro.
  if ((d.kind || '').startsWith('tool_')) return [];
  switch (d.kind) {
    case 'end':
      return [];
    case 'guardrails':
      return [
        { id: 'ok', label: t('AI_AGENTS.FLOW.OK') },
        { id: 'blocked', label: t('AI_AGENTS.FLOW.BLOCKED'), danger: true },
      ];
    case 'if_else':
      return [
        { id: 'true', label: d.trueLabel || 'Sim' },
        { id: 'false', label: d.falseLabel || 'Não', danger: true },
      ];
    case 'classify': {
      const cats = (d.categories || []).filter(c => c && c.name);
      if (cats.length) {
        return cats.map(c => ({ id: `cat:${c.name}`, label: c.name }));
      }
      return [{ id: null, label: null }];
    }
    default:
      return [{ id: null, label: null }];
  }
});

const isMulti = computed(() => outputs.value.length > 1);
const handleClass =
  '!size-2.5 !rounded-full !border-2 !border-white !bg-[#4285f4] !shadow-sm';
// Porta de ferramentas: âmbar (mesma cor dos nós de ferramenta), pra deixar
// claro que ali só entram ferramentas — não é a continuação do fluxo.
const toolsHandleClass =
  '!size-2.5 !rounded-full !border-2 !border-white !bg-[#f9ab00] !shadow-sm';
</script>

<template>
  <div
    class="relative min-w-[184px] max-w-[240px] rounded-2xl border bg-n-solid-1 px-3 py-2.5 shadow-md transition-all hover:shadow-lg"
    :class="[
      selected
        ? 'border-n-brand ring-2 ring-n-brand/40'
        : isFloating
          ? 'border-dashed border-n-amber-8'
          : 'border-n-weak',
      isToolOff ? 'opacity-50 grayscale' : '',
    ]"
  >
    <!-- Aviso de node solto: precisa entrar no fluxo (ligar a entrada). -->
    <span
      v-if="isFloating"
      v-tooltip="t('AI_AGENTS.FLOW.NODE_DISCONNECTED')"
      class="absolute -right-1.5 -top-1.5 z-10 flex size-4 items-center justify-center rounded-full bg-n-amber-9 text-white shadow"
    >
      <span class="i-lucide-unlink text-[9px]" />
    </span>

    <Handle
      v-if="hasInput"
      type="target"
      :position="isTool ? Position.Top : Position.Left"
      :class="isTool ? toolsHandleClass : handleClass"
    />

    <div class="flex items-center gap-2.5">
      <span
        class="flex size-8 shrink-0 items-center justify-center rounded-xl"
        :style="{ backgroundColor: tint(color), color }"
      >
        <span :class="meta.icon || 'i-lucide-box'" class="text-base" />
      </span>
      <div class="min-w-0">
        <p class="truncate text-sm font-semibold text-n-slate-12">
          {{ data.label || meta.label }}
        </p>
        <p v-if="data.subtitle" class="truncate text-xs text-n-slate-10">
          {{ data.subtitle }}
        </p>
      </div>
    </div>

    <!-- Badges: resumo visual da config do node -->
    <div v-if="badges.length" class="mt-2 flex flex-wrap gap-1">
      <span
        v-for="(b, i) in badges"
        :key="i"
        class="rounded-md px-1.5 py-0.5 text-[10px] font-medium"
        :style="{
          backgroundColor: tint(TONE_HEX[b.tone] || TONE_HEX.default, '24'),
          color: TONE_HEX[b.tone] || TONE_HEX.default,
        }"
      >
        {{ b.label }}
      </span>
    </div>

    <!-- Agente: duas saídas tipadas (fluxo à direita, ferramentas embaixo). O
         significado vem da posição + cor + tooltip (sem poluir com texto). -->
    <template v-if="isAgent">
      <Handle
        v-tooltip.right="t('AI_AGENTS.FLOW.OUT_FLOW')"
        type="source"
        :position="Position.Right"
        :class="handleClass"
      />
      <Handle
        id="tools"
        v-tooltip.bottom="t('AI_AGENTS.FLOW.OUT_TOOLS')"
        type="source"
        :position="Position.Bottom"
        :class="toolsHandleClass"
      />
    </template>

    <!-- Saída única: alça centralizada à direita -->
    <Handle
      v-else-if="outputs.length === 1"
      type="source"
      :position="Position.Right"
      :class="handleClass"
    />

    <!-- Múltiplas saídas: uma linha (rótulo + alça) por caminho -->
    <div v-else-if="isMulti" class="mt-2 flex flex-col gap-1.5">
      <div
        v-for="o in outputs"
        :key="o.id"
        class="relative flex items-center justify-end pr-2 text-[10px]"
        :class="o.danger ? 'text-n-ruby-9' : 'text-n-slate-11'"
      >
        <span class="truncate">{{ o.label }}</span>
        <Handle
          :id="o.id"
          type="source"
          :position="Position.Right"
          :class="handleClass"
          class="!relative !right-[-14px] !top-auto !translate-y-0"
        />
      </div>
    </div>
  </div>
</template>
